import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { PRIORITIES, REQ_STATUSES } from "./projectUtils";
import AttachmentPicker from "./AttachmentPicker";

const CATEGORIES = ["Functional", "Non-Functional", "UI/UX", "Integration", "Security", "Performance", "Other"];

const empty = {
  title: "", description: "", category: "Functional", priority: "Medium",
  assigned_to: "", deadline: "", status: "Draft", approval_status: "Pending", attachments: [],
};

export default function RequirementForm({ open, onOpenChange, initial, projectId, users = [], onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setForm(initial ? { ...empty, ...initial } : empty); }, [open, initial]);

  const teamUsers = users.filter(u => ["project_manager", "developer", "tester", "admin", "manager"].includes(u.role));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/requirements/${initial.id}`, form);
      else await api.post(`/projects/${projectId}/requirements`, form);
      toast.success(initial ? "Updated" : "Requirement added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFFFF] border-[#E4E7EC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="req-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{initial ? "Edit Requirement" : "New Requirement"}</DialogTitle>
          <DialogDescription className="text-neutral-600 text-xs">Capture what the client needs and route it for approval.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Title" required span={2}><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="rf-title" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Category">
            <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="rf-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Priority">
            <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="rf-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{PRIORITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Assigned To">
            <Select value={form.assigned_to || "none"} onValueChange={v => setForm({...form, assigned_to: v === "none" ? "" : v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="rf-assign"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">
                <SelectItem value="none">— Unassigned —</SelectItem>
                {teamUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Deadline"><Input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="rf-deadline" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Status">
            <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
              <SelectTrigger className="bg-[#FFFFFF] border-[#E4E7EC]" data-testid="rf-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#FFFFFF] border-[#E4E7EC]">{REQ_STATUSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Description" span={2}><Textarea rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="rf-desc" className="bg-[#FFFFFF] border-[#E4E7EC]" /></F>
          <F label="Attachments" span={2}><AttachmentPicker value={form.attachments} onChange={(a) => setForm({...form, attachments: a})} testid="rf-att" /></F>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="rf-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || !form.title.trim()} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="rf-save">
            {busy ? "Saving..." : (initial ? "Save Changes" : "Add Requirement")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({label, required, children, span = 1}) {
  return (
    <div className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
      <Label className="text-[10px] uppercase font-mono tracking-wider text-neutral-500">
        {label}{required && <span className="text-rose-400 ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
